<template>
  <div>
    <el-form
      @submit.prevent="validateSubmit('billForm')"
      novalidate
      :model="vendorBill"
      :rules="rules"
      label-position="top"
      ref="billForm"
      class="flex flex-col items-center"
    >
      <div class="input-section">
        <div class="flex flex-col md:flex-row justify-end">
          <div class="md:w-3/12 m-2">
            <horizontal-button
              :title="$t('actions.saveChanges')"
              isSuccess
              faIcon="save"
              :disabled="saving"
              @click.prevent="validateSubmit('billForm')"
            />
          </div>

          <div class="md:w-3/12 m-2" v-if="enableClearForm">
            <horizontal-button
              :title="$t('actions.clearButton')"
              isDanger
              faIcon="trash"
              @click.prevent="clearForm('billForm')"
            />
          </div>
        </div>
      </div>

      <!-- @submit errors -->
      <alert-badge
        isDanger
        :title="error.title"
        :body="error.body"
        @dismissed="
          () => {
            error.title = ''
            error.body = ''
          }
        "
      />

      <!-- Item Name -->
      <div class="input-section">
        <h1>{{ t('sections.newBill') }}</h1>

        <el-form-item :label="t('vendorName')" prop="vendorName" class="input-label">
          <el-select v-model="vendorBill.vendorID" filterable clearable class="w-full" required>
            <el-option
              v-for="vendor in vendors"
              :key="vendor.id"
              :label="vendor.name"
              :value="vendor.id"
            >
              <span :class="{ 'float-right': $ctx.getDir() == 'rtl' }">
                {{ vendor.name }}
              </span>
            </el-option>
          </el-select>
        </el-form-item>
      </div>

      <!-- Bill Date, Due Date, Vendor Bill Number -->
      <div class="input-section">
        <div class="input-group">
          <el-form-item :label="t('billDate')" prop="billDate" class="input-label" style="margin: 5px auto">
            <el-date-picker
              id="bill-date"
              v-model="vendorBill.billDate"
              type="date"
              :placeholder="t('billDate')"
              style="width: 100%"
            />
          </el-form-item>
          <el-form-item :label="t('dueDate')" prop="billDueDate" class="input-label" style="margin: 5px auto">
            <el-date-picker
              id="due-date"
              v-model="vendorBill.billDueDate"
              type="date"
              :placeholder="t('dueDate')"
              style="width: 100%"
            />
          </el-form-item>
          <el-form-item :label="t('vendorBillNumber')" prop="vendorBillNumber" class="input-label" style="margin: 5px auto">
            <el-input
              id="vendor-bill-number"
              v-model="vendorBill.vendorBillNumber"
              :placeholder="t('vendorBillNumber')"
              maxlength="100"
              clearable
            />
          </el-form-item>
        </div>
      </div>

      <!-- bill amount -->
      <div class="input-section">
        <el-form-item :label="t('totalAmount')" prop="totalDueAmount" class="input-label">
          <money-input
            id="total-amount"
            :currency="$ctx.currency"
            v-model="vendorBill.totalDueAmount"
            @dinero-created="(dinero) => (vendorBill.totalDueDinero = dinero)"
            ref="totalAmountRef"
          />
        </el-form-item>
        <div class="input-group">
          <el-form-item :label="t('paidAmount')" prop="totalPaidAmount" class="input-label">
            <money-input
              id="total-paid"
              :currency="$ctx.currency"
              v-model="vendorBill.totalPaidAmount"
              @dinero-created="(dinero) => (vendorBill.totalPaidDinero = dinero)"
              ref="paidAmountRef"
              :key="JSON.stringify(fullPaid)"
            />
          </el-form-item>
          <el-form-item class="input-label">
            <horizontal-button
              :title="$t('fullyPaid')"
              :isSuccess="true"
              @click.prevent="fullPay"
              class="fully-paid-button"
            />
          </el-form-item>
        </div>
        <div class="w-full p-0">
          <el-form-item :label="t('paidFrom')" prop="requestOnlyPayFromAccountID" class="input-label">
              <el-select
              id="credit-account"
              v-model="vendorBill.requestOnlyPayFromAccountID"
              :trigger-on-focus="false"
              value-key="name"
              class="w-full"
              filterable
            >
              <el-option
                :key="registerAccount.id"
                :label="$ctx.locale === 'ar' ? registerAccount.arName : registerAccount.enName"
                :value="registerAccount.id"
              >
                <div class="w-full bg-green-100">
                  {{ $ctx.locale === 'ar' ? registerAccount.arName : registerAccount.enName }}
                </div>
              </el-option>

              <el-option
                v-for="safe in safes"
                :key="safe.id"
                :label="$ctx.locale === 'ar' ? safe.arName : safe.enName"
                :value="safe.id"
              >
                <div class="w-full bg-green-100">
                  {{ $ctx.locale === 'ar' ? safe.arName : safe.enName }}
                </div>
              </el-option>

              <el-option
                v-for="bank in banks"
                :key="bank.id"
                :label="$ctx.locale === 'ar' ? bank.arName : bank.enName"
                :value="bank.id"
              >
                <div class="w-full bg-green-100">
                  {{ $ctx.locale === 'ar' ? bank.arName : bank.enName }}
                </div>
              </el-option>

              <el-option
                v-for="option in accounts"
                :key="option.id"
                :label="$ctx.locale === 'ar' ? option.arName : option.enName"
                :value="option.id"
              ></el-option>
            </el-select>
          </el-form-item>
        </div>
      </div>

      <!-- What's For and Debited Account -->
      <div class="input-section">
        <div class="input-group">
          <el-form-item :label="t('whatsFor')" prop="whatsFor" class="input-label" style="margin: 5px auto">
            <el-input
              id="whats-for"
              v-model="vendorBill.whatsFor"
              :placeholder="t('whatsFor')"
              maxlength="500"
              clearable
            />
          </el-form-item>

          <el-form-item :label="t('debitedAccount')" prop="debitedAccountID" class="input-label" style="margin: 5px auto">
            <el-select
              id="debited-account"
              v-model="vendorBill.debitedAccountID"
              :trigger-on-focus="false"
              value-key="id"
              class="w-full"
              filterable
              remote
              clearable
              :remote-method="searchDebitAccounts"
            >
              <el-option
                v-for="option in debitAccounts"
                :key="option.id"
                :label="$ctx.locale === 'ar' ? option.arName : option.enName"
                :value="option.id"
              ></el-option>
            </el-select>
          </el-form-item>
        </div>
      </div>

      <!-- Notes -->
      <div class="input-section">
        <el-form-item :label="t('notes')" prop="notes" class="input-label">
          <el-input
            id="notes"
            v-model="vendorBill.notes"
            type="textarea"
            :placeholder="t('notes')"
            :rows="4"
            maxlength="1000"
            show-word-limit
          />
        </el-form-item>
      </div>

      <!-- Attachments -->
      <div class="input-section">
        <vendor-bill-attachment-input v-model="vendorBill.attachmentFiles" />
      </div>
    </el-form>

    <!-- Cashier PIN Dialog -->
    <el-dialog v-model="cashierPinDialogOpen" width="30%">
      <div>
        <!-- Enter the pin -->
        <div class="my-2">
          <p>{{ t('enterCashierPin') }}</p>
        </div>

        <!-- password input up to 4 chars -->
        <div class="my-2">
          <el-input
            id="cashier-pin"
            v-model="cashierPin"
            type="password"
            required
            show-password
            autofocus
            dir="ltr"
            :maxlength="4"
            :minlength="4"
            ref="cashierPinInput"
            @keyup.enter="submitWithPin"
          />
        </div>

        <!-- submit button -->
        <div class="flex items-center justify-between mb-2">
          <horizontal-button
            class="text-lg"
            :title="t('submit')"
            @click="submitWithPin"
            :disabled="saving"
            :rounded="false"
            :isSuccess="true"
          />
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent, onBeforeMount, ref, nextTick } from 'vue'

// models
import VendorBill, { vendorBillI18nMessages } from '@/models/vendor/VendorBill'
import Vendor from '@/models/vendor/Vendor'

// components
import HorizontalButton from '@/components/HorizontalButton.vue'
import AlertBadge from '@/components/AlertBadge.vue'
import { ElForm } from 'element-plus'
import MoneyInput from '@/components/form/MoneyInput.vue'
import VendorBillAttachmentInput from '@/components/file-manager/VendorBillAttachmentInput.vue'

import { useI18n } from 'vue-i18n'
import { Account } from '@/models/accounting/Account'
import { useHTTP } from '@/plugins/http'
import { useContext } from '@/plugins/context'
import { useSettingsStore } from '@/stores/settingsStore'

interface Option {
  name: string
  values: string[]
}

export default defineComponent({
  name: 'bill-form',

  setup() {
    const messages = {
      en: {
        ...vendorBillI18nMessages.en,

        sections: {
          newBill: 'New Bill'
        },
        fullyPaid: 'Fully Paid',
        errYouMustSelectAnAccount: 'You must select an account to pay from',
        paidFrom: 'Paid from',
        enterCashierPin: 'Enter cashier PIN',
        submit: 'Submit',
        errCashierPinRequired: 'Cashier PIN is required'
      },

      ar: {
        ...vendorBillI18nMessages.ar,
        sections: {
          newBill: 'فاتورة جديدة'
        },
        fullyPaid: 'دفع المبلغ الكامل',
        errYouMustSelectAnAccount: 'يجب اختيار حساب للدفع',
        paidFrom: 'حساب الدفع',
        enterCashierPin: 'ادخل رمز الكاشير الخاص بك',
        submit: 'حفظ',
        errCashierPinRequired: 'يرجى ادخال رمز الكاشير'
      }
    }

    const { t } = useI18n({ messages, useScope: 'global' })
    const http = useHTTP();
    const ctx = useContext();

    const rules = {
      vendorID: [{ required: true, message: t('validation.required') }],
      totalPaidAmount: [{ required: true, message: t('validation.required') }],
      totalDueAmount: [{ required: true, message: t('validation.required') }],
      billDate: [{ required: true, message: t('validation.required') }],
      debitedAccountID: [{ required: true, message: t('validation.required') }]
    }

    const registerAccount = ref(new Account())
    const safes = ref<Account[]>([])
    const banks = ref<Account[]>([])

    onBeforeMount(async () => {
      try {
        registerAccount.value = Account.from(await http.get<Account>(`${Account.ENDPOINT}/${ctx.currentRegister.accountID}`))
        const safesResp = await http.get<Account[]>('accounting/accounts/safes?skip_balance=true')
        safes.value = safesResp.map((s) => Account.from(s))
        const banksResp = await http.get<Account[]>('accounting/accounts/banks?skip_balance=true')
        banks.value = banksResp.map((b) => Account.from(b))
      } catch (err) {
        console.log(err)
      }
    })


    return { t, rules, registerAccount, safes, banks }
  },

  components: {
    HorizontalButton,
    AlertBadge,
    MoneyInput,
    VendorBillAttachmentInput
  },
  props: {
    initBill: {
      type: VendorBill,
      required: true
    },
    successMsg: {
      type: String,
      required: true
    },
    submitAction: {
      type: Function,
      required: true
    },
    enableClearForm: {
      type: Boolean,
      default: false
    },
    clearFormOnSubmit: {
      type: Boolean,
      default: false
    }
  },
  // I have the choice to use v-model but would make this code
  // incredibly complicated!
  // I chose to alias/copy props, even though it might be against vue style guide
  // the parent hence passes an initial copy of the item and variants and DOES NOT
  // expect those inital copies to be synchronized!
  data() {
    const accounts: Account[] = []
    const debitAccounts: Account[] = []

    return {
      vendorBill: this.initBill,
      vendors: new Array<Vendor>(),
      fullPaid: false,
      error: { title: '', body: '' },
      accounts,
      debitAccounts,
      cashierPinDialogOpen: false,
      cashierPin: '',
      saving: false
    }
  },
  async beforeMount() {
    try {
      const vendorResp = await this.$http.get<Vendor[]>(Vendor.ENDPOINT)
      this.vendors = vendorResp.map((vendor) => Vendor.from(vendor))
    } catch (error) {
      this.error.title = error?.title
      this.error.body = error?.body
    }
  },
  methods: {
    async searchAccounts(query: string) {
      try {
        const url = `${Account.ENDPOINT}?q=${query}`
        this.accounts = await this.$http.get<Account[]>(url)
      } catch (error) {
        this.$alertModal.showDanger({ title: error.title })
        this.accounts = []
      }
    },
    async searchDebitAccounts(query: string) {
      try {
        const url = `${Account.ENDPOINT}?q=${query}`
        this.debitAccounts = await this.$http.get<Account[]>(url)
      } catch (error) {
        this.$alertModal.showDanger({ title: error.title })
        this.debitAccounts = []
      }
    },
    clearForm(formName: string) {
      const form = this.$refs[formName] as typeof ElForm
      this.vendorBill = new VendorBill()
      this.fullPaid = false
      this.$globalEmitter.emit('money-input-reset')
      form.resetFields()
      this.fullPay()
    },

    async validateSubmit(formName: string) {
      this.vendorBill.currency = this.vendorBill.totalPaidDinero?.getCurrency()
      this.vendorBill.precision = this.vendorBill.totalPaidDinero?.getPrecision()
      this.vendorBill.totalBalanceAmount =
      this.vendorBill.totalDueAmount - this.vendorBill.totalPaidAmount
      if (this.vendorBill.totalPaidAmount > 0 && !this.vendorBill.requestOnlyPayFromAccountID) {
        this.error.title = this.t("errYouMustSelectAnAccount")
        this.error.body = ""
        document.getElementById('main')?.scrollTo({ top: 0 })
        return false
      }

      const form = this.$refs[formName] as typeof ElForm
      form?.validate(async (valid: boolean) => {
        if (!valid) {
          document.getElementById('main')?.scrollTo({ top: 0 })
          return false
        }

        // Check if cashier PIN is required
        if (useSettingsStore().getValue('is_cashier_pin_required') == 'true') {
          this.cashierPinDialogOpen = true
          nextTick(() => {
            setTimeout(() => {
              const pinInput = this.$refs.cashierPinInput as any
              if (pinInput) {
                pinInput.focus()
              }
            }, 0)
          })
          return
        }

        // If PIN not required, submit directly.
        // Block double-submits while the request is in flight (#1141)
        if (this.saving) return false
        this.saving = true
        let resp
        try {
          resp = await this.submitAction(this.vendorBill)
        } finally {
          this.saving = false
        }
        if (resp) {
          this.error.title = resp?.title
          this.error.body = resp?.body
          document.getElementById('main')?.scrollTo({ top: 0 })
          return false
        } else {
          if (this.clearFormOnSubmit) {
            this.clearForm(formName)
          }

          this.$alertModal.showSuccess({ title: this.successMsg, body: '' })
        }
        return true
      })
    },

    async submitWithPin() {
      if (this.saving) return
      // Validate PIN is entered
      if (!this.cashierPin || this.cashierPin.length === 0) {
        this.cashierPinDialogOpen = false
        this.error.title = this.t('errCashierPinRequired')
        this.error.body = ''
        document.getElementById('main')?.scrollTo({ top: 0 })
        return
      }

      // Submit with PIN in headers
      this.saving = true
      let resp
      try {
        resp = await this.submitAction(this.vendorBill, this.cashierPin)
      } finally {
        this.saving = false
      }
      this.cashierPinDialogOpen = false
      this.cashierPin = '' // Clear PIN

      if (resp) {
        this.error.title = resp?.title
        this.error.body = resp?.body
        document.getElementById('main')?.scrollTo({ top: 0 })
        return false
      } else {
        if (this.clearFormOnSubmit) {
          this.clearForm('billForm')
        }

        this.$alertModal.showSuccess({ title: this.successMsg, body: '' })
      }
    },

    fullPay() {
      this.vendorBill.totalPaidAmount = this.vendorBill.totalDueAmount
      this.fullPaid = true
    }
  }
})
</script>

<style scoped>
h1 {
  @apply text-lg py-3 font-bold;
}

h2 {
  @apply py-3 font-bold;
}

h3 {
  @apply py-3 font-bold;
}

.input-group {
  @apply flex flex-col justify-between w-full items-end;
}

.input-group-row {
  @apply flex justify-between w-full;
}

.input-group-row > label {
  @apply w-6/12;
}

.input-section {
  @apply rounded w-full justify-center;
}

@screen lg {
  .input-section {
    @apply w-7/12;
  }
}

@screen md {
  .input-section {
    @apply flex-row;
  }

  .input-group {
    @apply flex-row;
  }

  .input-group > .input-label {
    @apply w-6/12;
  }
}

.remove-button {
  @apply text-lg;
  color: var(--danger-text-color);
  align-self: center;
  justify-self: center;
  cursor: pointer;
  margin: 0.5rem;
}

.remove-button:hover {
  color: var(--danger-hover-bg-color);
  align-self: center;
  justify-self: center;
}

.fully-paid-button {
  height: 40px;
}

hr.solid {
  border-top: 1px solid var(--tertiary-bg-color);
}

.hide {
  display: none;
}

.input:hover + .hide {
  display: flex;
  justify-content: center;
}

.danger {
  color: var(--danger-text-color);
  background: var(--danger-bg-color);
}

.success {
  color: var(--success-text-color);
  background: var(--success-bg-color);
}

td,
th {
  border: 1px solid #dddddd;
  text-align: left;
  padding: 8px;
  text-align: center;
}

tr:nth-child(even) {
  background-color: #f3f3f3;
}
</style>
